
import {
  convertPageStructureIndexToTreeIndex,
  getPageStructureFromTree
} from "@/lib/singularity/page-structure/page-structure";
import { documentStore, singularityStore, uiStore } from "@/store/store";
import Vue from "vue";
import { Component } from "vue-property-decorator";
import PageStructureSectionNew from "./PageStructureSectionNew.vue";
import draggable from "vuedraggable";
import { PageSection } from "@/lib/singularity/page-structure/page-structure";
import Icon from "@/components/UI/Icon.vue";
import PageStructureHeader from "./PageStructureHeader.vue";
import NiceButton from "@/components/UI/NiceButton.vue";
import { scrollToActiveElement } from "@/lib/canvas/scroll-to-active-element";
import { TreeNode } from "@/types/tree";
import { logEvent } from "@/util/tracking";

@Component({
  components: {
    PageStructureSectionNew,
    draggable,
    Icon,
    PageStructureHeader,
    NiceButton
  }
})
export default class PageStructureNew extends Vue {
  isDragging = false;
  isScrolling = false;
  currentlyDraggedNodeId: TreeNode["id"] | null = null;

  get activeElement() {
    return uiStore.activeElementId;
  }

  get structure() {
    return getPageStructureFromTree(documentStore.document.tree);
  }

  get pageTitle() {
    return documentStore.document.documentMeta.titleLabel;
  }

  get isOpeningPage() {
    return singularityStore.isOpeningPageId;
  }

  isActive(nodeId: TreeNode["id"]) {
    if (uiStore.inlineEditingContext) {
      return uiStore.isElementBeingEditedInline(nodeId);
    } else {
      return uiStore.activeElementId === nodeId;
    }
  }

  get draggableOptions() {
    return {
      ghostClass: "page-structure-section--dragging",
      dragClass: "page-structure-section--drag-active",
      setData: (dataTransfer: DataTransfer) => {
        // Create empty/transparent drag image
        const emptyDiv = document.createElement("div");
        emptyDiv.style.width = "1px";
        emptyDiv.style.height = "1px";
        emptyDiv.style.opacity = "0";
        document.body.appendChild(emptyDiv);
        dataTransfer.setDragImage(emptyDiv, 0, 0);
        setTimeout(() => {
          if (document.body.contains(emptyDiv)) {
            document.body.removeChild(emptyDiv);
          }
        }, 0);
      }
    };
  }

  onDragStart(event: any) {
    this.isDragging = true;
    // Use the oldIndex from the drag event to get the dragged element
    const draggedIndex = event.oldIndex;
    this.currentlyDraggedNodeId = this.structure[draggedIndex]?.nodeId || null;
  }

  onDragEnd() {
    this.isDragging = false;
    this.currentlyDraggedNodeId = null;
  }

  handleChange(
    // https://github.com/SortableJS/Vue.Draggable#events
    event: {
      moved?: { newIndex: number; oldIndex: number; element: PageSection };
    }
  ) {
    if (event.moved) {
      const treeNodeIdToMove = event.moved.element.nodeId;
      const newIndex = convertPageStructureIndexToTreeIndex(
        event.moved.newIndex,
        documentStore.document.tree
      );
      documentStore.moveElement({
        draggedElementId: treeNodeIdToMove,
        droppedOnElementId: documentStore.document.tree.root.id,
        index: newIndex
      });

      uiStore.activateElement(treeNodeIdToMove);

      /*
      when dragging an active section, it won't be scrolled into view by uiStore.activateElement
      because uiStore.activateElement skips scrolling if the activated element was already active
      */
      scrollToActiveElement(true);
    }
  }

  addSection() {
    singularityStore.setSectionAddOrSwapPanelState({
      mode: "add_after",
      sectionId: this.structure.length
        ? this.structure[this.structure.length - 1].nodeId
        : null
    });

    singularityStore.setSingularityLeftPanel("add_new_section");

    logEvent("FutureLayer Page Structure Add Section Click", {
      pageTitle: this.pageTitle,
      currentSectionCount: this.structure.length
    });
  }
}
